import classNames from "classnames";

import MultimediaWithFlag from "@stadium/shared/src/components/shared/multimedia";

import Pill from "@stadium/ui/src/components/pill";
import ProductPrice from "@sharedComponents/cards/product/price";
import ProductColorPills from "@sharedComponents/cards/product/color-pills";
import ProductBadges from "@components/swagmagic/dynamic/cards/product/badges";

import {
  PL_06,
  PL_07,
  PL_10,
  PL_11,
  PL_12,
  DESCRIPTION_CARD_VARIANT,
  PL_01,
  ProductListingVariant,
} from "@helpers/plasmic/slices";
import { isDSDProduct } from "@helpers/products/types";
import { SEARCH_SOURCE } from "@helpers/plasmic/products";

import styles from "./main.module.scss";

import NoImage from "@assets/images/no-image.svg";

export type ProductCardProps = {
  showBadgesAndColorOptions: boolean;
  variant: ProductListingVariant;
  product: any;
  isLockerShop: boolean;
  source?: string;
};

const ProductCard = ({
  variant,
  product,
  showBadgesAndColorOptions,
  isLockerShop,
  source = SEARCH_SOURCE,
}: ProductCardProps) => {
  const {
    baseImage,
    name,
    minOrderableQuantity,
    intent,
    ecoFriendly,
    sustainable,
    shippedFromStadium,
    coreOnDemand,
    storeProduct,
    servingInfo,
    colorOptions,
    colorOptionsCount,
  } = product;

  const showColorDots = !!colorOptions?.length;

  const showCoreOnDemand = coreOnDemand && isLockerShop;

  const hasBadge =
    ecoFriendly ||
    sustainable ||
    shippedFromStadium ||
    showCoreOnDemand ||
    !!storeProduct?.embroidery;

  const isDescriptionCard = DESCRIPTION_CARD_VARIANT.includes(variant);

  const productImageComponent = (
    <MultimediaWithFlag
      type="image"
      src={baseImage || NoImage}
      alt={`${name} for`}
      className={`${styles.cardImage} ${baseImage ? "" : styles.imageNotAvailable}`}
      loading="lazy"
    />
  );

  const colorPillsComponent = showBadgesAndColorOptions && showColorDots && (
    <ProductColorPills
      colors={colorOptions}
      totalColorsCount={colorOptionsCount}
      containerClass={styles.tileColorPalatte}
    />
  );

  const badgesComponents = showBadgesAndColorOptions && (
    <>
      {hasBadge && (
        <ProductBadges
          {...product}
          isLockerShop={isLockerShop}
          showCoreOnDemand={showCoreOnDemand}
        />
      )}
      {isDSDProduct(intent) && servingInfo && (
        <Pill color="light" className={styles.servePill}>
          SERVES {servingInfo}
        </Pill>
      )}
    </>
  );

  const productPriceComponent = (
    <ProductPrice product={product} productPriceClass="" intent={intent} source={source} />
  );

  if (isDescriptionCard) {
    const description = product.description;

    return (
      <div
        className={classNames({
          [styles.largeDescriptionCard]: variant === PL_07 || variant === PL_10,
          [styles.smallDescriptionCard]:
            variant === PL_06 || variant === PL_11 || variant === PL_12,
        })}
      >
        <div className={`${styles.productImageBlock} product-image-block`}>
          <div className={styles.cardImageHolder}>{productImageComponent}</div>
          {badgesComponents}
        </div>
        <div className={styles.cardContentHolder}>
          <div className={classNames(styles.cardContentHeader, styles.cardContentHeaderAutoHeight)}>
            <div className={styles.cardContentTitle}>{name}</div>
            <div
              className={styles.cardContentDescription}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
          <div className={styles.cardContentFooter}>
            <div className={styles.cardContentPrice}>{productPriceComponent}</div>
            {colorPillsComponent && (
              <div className={styles.cardColorOption}>{colorPillsComponent}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { brand, brands = [] } = product;
  const brandName = brand?.displayName || brands?.find((brand) => brand.name)?.name || "";

  return (
    <div className={`${styles.productCardHolder} ${variant === PL_01 ? styles.wideBlockCard : ""}`}>
      <div className={`${styles.productImageBlock} product-image-block`}>
        <div className={styles.cardImageHolder}>{productImageComponent}</div>
        {badgesComponents}
      </div>
      <div className={styles.prdouctCardContent}>
        <div className={styles.cardContentHeader}>
          <div className={styles.brandName}>{brandName}</div>
          <div className={styles.productTitle}>{name}</div>
        </div>
        <div className={styles.cardContentFooter}>
          <div className={styles.cardPriceRow}>
            <div className={styles.productPrice}>{productPriceComponent}</div>
            <div className={styles.productQuantity}>Min {minOrderableQuantity}</div>
          </div>
          {colorPillsComponent && (
            <div className={styles.cardColorOption}>{colorPillsComponent}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
